import React from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme";
import NotFound from "@/pages/not-found";

// Page Imports
import { AuthPage } from "@/pages/auth";
import { Dashboard } from "@/pages/dashboard";
import { Stores } from "@/pages/stores";
import { Flows } from "@/pages/flows";
import { FlowNew } from "@/pages/flow-new";
import { Logs } from "@/pages/logs";
import { Reconciliation } from "@/pages/reconciliation";
import { ReconciliationDetail } from "@/pages/reconciliation-detail";
import { Import } from "@/pages/import";
import { Scraper } from "@/pages/scraper";
import { CustomerAutomationsList } from "@/pages/customer-automations";
import { CustomerAutomationBuilder } from "@/pages/customer-automation-builder";
import { CustomerAutomationDetail } from "@/pages/customer-automation-detail";
import { MessagingIntegrations } from "@/pages/messaging-integrations";
import { ProductUpdates } from "@/pages/product-updates";
import { WheelPros } from "@/pages/wheel-pros";
import { WheelProsSync } from "@/pages/wheel-pros-sync";
import { WheelProsImportLog } from "@/pages/wheel-pros-import-log";
import { Collections } from "@/pages/collections";
import { Fitment } from "@/pages/fitment";
import { MetafieldUpdate } from "@/pages/metafield-update";
import { TagUpdate } from "@/pages/tag-update";
import { WheelProsSettings } from "@/pages/wheel-pros-settings";
import { AdminPage } from "@/pages/admin";
import { OffroadImportsSettings } from "@/pages/offroadimports-settings";
import { PriceScrape } from "@/pages/price-scrape";
import { Billing } from "@/pages/billing";
import { ProductTypeFixer } from "@/pages/product-type-fixer";
import { SkuCreator } from "@/pages/sku-creator";
import { ShopifyFileUpload } from "@/pages/shopify-file-upload";
import { Copilot } from "@/pages/copilot";
import { ShippingRates } from "@/pages/shipping-rates";
import { InventorySync } from "@/pages/inventory-sync";
import { PoSuppliers } from "@/pages/po-suppliers";
import { PoPurchaseOrders } from "@/pages/po-purchase-orders";
import { PoPurchaseOrderEdit } from "@/pages/po-purchase-order-edit";
import { PoDraftImport } from "@/pages/po-draft-import";
import { NotifyMe } from "@/pages/notify-me";
import { GamePopup } from "@/pages/game-popup";
import { useGetMe } from "@workspace/api-client-react";
import { useAuthHeaders, useAuthStore } from "@/lib/auth-store";

const ADMIN_EMAIL = 'marketing@bove.com.au';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AdminRoute({ component: Component, allowedFeature }: { component: React.ComponentType; allowedFeature?: string }) {
  const [, setLocation] = useLocation();
  const token = useAuthStore(s => s.token);
  const authOpts = useAuthHeaders();
  const accountQueryScope = React.useMemo(() => crypto.randomUUID(), [token]);
  const { data: me, isLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false, queryKey: ['/api/auth/me', 'route-access', accountQueryScope] },
  });

  const isAdmin = me?.email === ADMIN_EMAIL || (me as any)?.role === 'admin';
  const featureAllowed = !!allowedFeature && (me as any)?.featureFlags?.[allowedFeature] === true;
  const permitted = !!me && (isAdmin || featureAllowed);
  React.useEffect(() => {
    if (!isLoading && !permitted) setLocation('/');
  }, [isLoading, permitted, setLocation]);

  if (isLoading || !permitted) return null;

  return <Component key={accountQueryScope} />;
}

function Router() {
  return (
    <Switch>
      <Route path="/auth/login"><AuthPage /></Route>
      <Route path="/auth/register"><AuthPage isRegister={true} /></Route>

      <Route path="/" component={Dashboard} />
      <Route path="/dashboard" component={Dashboard} />
      <Route path="/stores" component={Stores} />
      <Route path="/flows" component={Flows} />
      <Route path="/flows/new" component={FlowNew} />
      <Route path="/logs" component={Logs} />
      <Route path="/import" component={Import} />
      <Route path="/scraper" component={Scraper} />
      <Route path="/price-scrape" component={PriceScrape} />
      <Route path="/customer-automations" component={CustomerAutomationsList} />
      <Route path="/customer-automations/new" component={CustomerAutomationBuilder} />
      <Route path="/customer-automations/:id/edit" component={CustomerAutomationBuilder} />
      <Route path="/customer-automations/:id" component={CustomerAutomationDetail} />
      <Route path="/product-updates" component={ProductUpdates} />
      <Route path="/wheel-pros/sync" component={WheelProsSync} />
      <Route path="/collections" component={Collections} />
      <Route path="/fitment" component={Fitment} />
      <Route path="/metafield-update" component={MetafieldUpdate} />
      <Route path="/tag-update" component={TagUpdate} />
      <Route path="/product-type-fixer" component={ProductTypeFixer} />
      <Route path="/sku-creator" component={SkuCreator} />
      <Route path="/shopify-file-upload" component={ShopifyFileUpload} />
      <Route path="/copilot" component={Copilot} />

      {/* Admin-only routes */}
      <Route path="/admin"><AdminRoute component={AdminPage} /></Route>
      <Route path="/reconciliation"><AdminRoute component={Reconciliation} /></Route>
      <Route path="/reconciliation/:id"><AdminRoute component={ReconciliationDetail} /></Route>
      <Route path="/wheel-pros"><AdminRoute component={WheelPros} /></Route>
      <Route path="/wheel-pros/import-log"><AdminRoute component={WheelProsImportLog} /></Route>
      <Route path="/settings/messaging"><AdminRoute component={MessagingIntegrations} /></Route>
      <Route path="/settings/wheel-pros"><AdminRoute component={WheelProsSettings} allowedFeature="wheel_pros_settings" /></Route>
      <Route path="/offroad-imports" component={OffroadImportsSettings} />
      <Route path="/offroad-imports/sync" component={OffroadImportsSettings} />
      <Route path="/offroad-imports/import" component={OffroadImportsSettings} />
      <Route path="/settings/billing" component={Billing} />
      <Route path="/settings/shipping-rates" component={ShippingRates} />
      <Route path="/settings/inventory-sync" component={InventorySync} />
      <Route path="/settings/dynamic-wheel" component={InventorySync} />
      <Route path="/po/suppliers" component={PoSuppliers} />
      <Route path="/po/purchase-orders" component={PoPurchaseOrders} />
      <Route path="/po/purchase-orders/:id/edit" component={PoPurchaseOrderEdit} />
      <Route path="/po/draft-import" component={PoDraftImport} />
      <Route path="/notify-me" component={NotifyMe} />
      <Route path="/game-popup" component={GamePopup} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <Router />
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
